/**
 * ExternalAnimationLoader - loads external animation sources with optional retargeting.
 */
import * as THREE from 'three';

import { AssetPath } from '../storage/storageProvider.js';
import { resourceManager } from '../utils/ResourceManager.js';

import { AnimationClipCache } from './AnimationClipCache.js';
import {
  retargetAnimationClips
} from './AnimationRetargeting.js';

import type { ExternalAnimationSource } from './AnimationTypes.js';
import type { SkeletonProfile } from './SkeletonProfile.js';
import type { ModelMeshComponent } from '../components/visual/ModelMeshComponent.js';
export async function loadExternalAnimationSources(
  sources: Array<string | ExternalAnimationSource>,
  targetSkeletonProfile: SkeletonProfile | null,
  modelMeshComponent: ModelMeshComponent
): Promise<void> {
  // Get target skeleton if retargeting is enabled
  const targetSkinnedMeshData = targetSkeletonProfile ? modelMeshComponent.getSkinnedMeshData() : null;
  const targetSkeleton = targetSkinnedMeshData?.skeleton ?? null;
  const targetSkinnedMesh = targetSkinnedMeshData?.skinnedMesh ?? null;

  if (targetSkeletonProfile && (!targetSkeleton || !targetSkinnedMesh)) {
    console.error('AnimationStateMachineComponent: Target skeleton profile specified but no skinned mesh found on target character');
    return;
  }

  // When retargeting with embedWorldTransforms, the retargeter captures
  // bones[0].parent.matrixWorld which includes ALL ancestor transforms.
  // Visual transforms (meshRotation, actor position, etc.) above the GLTF
  // model corrupt the bind-pose computation. Neutralize them for retargeting.
  const savedVisualTransforms: Array<{ obj: THREE.Object3D; pos: THREE.Vector3; quat: THREE.Quaternion; scale: THREE.Vector3 }> = [];
  const targetModel = targetSkeletonProfile ? modelMeshComponent.getModel() : null;
  if (targetModel) {
    let current: THREE.Object3D | null = targetModel.parent;
    while (current) {
      savedVisualTransforms.push({
        obj: current,
        pos: current.position.clone(),
        quat: current.quaternion.clone(),
        scale: current.scale.clone(),
      });
      current.position.set(0, 0, 0);
      current.quaternion.identity();
      current.scale.set(1, 1, 1);
      current = current.parent;
    }
    targetModel.updateWorldMatrix(true, true);
  }

  await Promise.all(sources.map(async (sourceConfig) => {
    try {
      // Parse source config (can be string URL or object with profile)
      const sourceUrl = typeof sourceConfig === 'string' ? sourceConfig : sourceConfig.url;
      const sourceProfileUrl = typeof sourceConfig === 'string' ? null : sourceConfig.skeletonProfile;

      // Load the source GLTF
      const modelPath = new AssetPath(sourceUrl);
      const gltf = await resourceManager.loadModel(modelPath);
      if (!gltf || !gltf.animations || gltf.animations.length === 0) {
        console.warn(`AnimationStateMachineComponent: No animations found in ${sourceUrl}`);
        return;
      }

      // If retargeting is enabled, load source profile and retarget
      if (targetSkeletonProfile && sourceProfileUrl && targetSkeleton && targetSkinnedMesh) {
        // Load source skeleton profile
        const sourceProfilePath = new AssetPath(sourceProfileUrl);
        const sourceProfile: SkeletonProfile | null = await resourceManager.loadJsonObject(sourceProfilePath);

        if (!sourceProfile) {
          console.error(`AnimationStateMachineComponent: Failed to load source profile from ${sourceProfileUrl}`);
          return;
        }

        // Check if source and target profiles are the same - if so, skip retargeting
        if (sourceProfile.name === targetSkeletonProfile.name) {
          console.log(`AnimationStateMachineComponent: Source and target use same profile "${sourceProfile.name}", skipping retargeting for ${gltf.animations.length} clips from ${sourceUrl}`);
          for (const clip of gltf.animations) {
            AnimationClipCache.instance.addClip(clip);
          }
          return;
        }

        // Get source skeleton from the GLTF
        const sourceScene = gltf.scene;
        let sourceSkinnedMesh: THREE.SkinnedMesh | undefined;
        sourceScene.traverse((node) => {
          if (node instanceof THREE.SkinnedMesh && node.skeleton && !sourceSkinnedMesh) {
            sourceSkinnedMesh = node as THREE.SkinnedMesh;
          }
        });

        if (!sourceSkinnedMesh || !sourceSkinnedMesh.skeleton) {
          console.error(`AnimationStateMachineComponent: No skeleton found in source GLTF ${sourceUrl}`);
          return;
        }

        const sourceSkeleton = sourceSkinnedMesh.skeleton;

        // Retarget all clips without mutating target skeleton topology.
        // Runtime bone-count changes can overflow preallocated skinning uniform buffers (WebGPU),
        // so synthetic root injection is intentionally disabled.
        const results = retargetAnimationClips(
          gltf.animations,
          sourceProfile,
          targetSkeletonProfile,
          sourceSkeleton,
          targetSkeleton,
          targetSkinnedMesh
        );

        // Add successfully retargeted clips to cache
        let successCount = 0;
        for (const result of results) {
          if (result.success && result.clip) {
            AnimationClipCache.instance.addClip(result.clip);
            successCount++;
          } else {
            console.warn(`AnimationStateMachineComponent: Failed to retarget clip "${result.originalName}" from ${sourceUrl}`);
          }
        }

        console.log(`AnimationStateMachineComponent: Retargeted ${successCount}/${gltf.animations.length} clips from ${sourceUrl}`);
      } else {
        // No retargeting - add clips as-is
        for (const clip of gltf.animations) {
          AnimationClipCache.instance.addClip(clip);
        }
        console.log(`AnimationStateMachineComponent: Loaded ${gltf.animations.length} clips from ${sourceUrl} (no retargeting)`);
      }
    } catch (e) {
      const sourceUrl = typeof sourceConfig === 'string' ? sourceConfig : sourceConfig.url;
      console.error(`AnimationStateMachineComponent: Failed to load external animation source: ${sourceUrl}`, e);
    }
  }));

  // Restore visual transforms after all retargeting is complete
  for (const saved of savedVisualTransforms) {
    saved.obj.position.copy(saved.pos);
    saved.obj.quaternion.copy(saved.quat);
    saved.obj.scale.copy(saved.scale);
  }
  if (targetModel) {
    targetModel.updateWorldMatrix(true, true);
  }
}
